// src/pages/auth/ForgotPasswordPage.tsx
//
// "Glömt lösenord?" pointed at /reset-password, which was not a route — so the
// link 404'd, and so did the link in the password-reset email the API sends
// (FRONTEND_BASE_URL + "/reset-password?token=..."). The whole reset feature
// existed on the backend and could not be reached from the product at all.
// This page is the request half; ResetPasswordPage is the half the email opens.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { getUserApi } from '@/lib/api/client';
import { toCompatError } from '@/lib/api/apiUtils';
import { friendlyAuthErrorText } from '@/lib/api/authErrors';
import { InfoBox } from '@/components/custom-ui/info-box';
import { AuthShell } from './AuthShell';

const ForgotPasswordPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        try {
            await getUserApi().forgotPassword({ email: email.trim() });
            setSent(true);
        } catch (err) {
            setError(friendlyAuthErrorText(toCompatError(err)));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (sent) {
        return (
            <AuthShell title="Kolla din e-post" subtitle="Vi har skickat en återställningslänk.">
                <div className="flex flex-col items-center text-center gap-4">
                    <div className="w-14 h-14 bg-[#A4C3B2] rounded-full flex items-center justify-center">
                        <CheckCircle2 className="w-7 h-7 text-[#1A4D4F]" />
                    </div>
                    <p className="text-sm text-gray-700">
                        Om det finns ett konto med <span className="font-semibold break-all">{email}</span> har vi
                        skickat en länk dit. Länken gäller i en timme.
                    </p>
                    <p className="text-xs text-gray-500">
                        Hittar du inget mejl? Titta i skräpposten, eller försök igen med en annan adress.
                    </p>
                    <Link
                        to="/"
                        className="mt-2 w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#1A4D4F] text-[#f5f5f5] font-semibold rounded-xl hover:bg-[#1A4D4F]/90 transition-all text-sm"
                    >
                        Till startsidan
                    </Link>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell
            title="Glömt lösenord?"
            subtitle="Ange din e-postadress så skickar vi en länk för att välja ett nytt."
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                {error && <InfoBox variant="error">{error}</InfoBox>}

                <div>
                    <label className="block text-xs font-semibold mb-1.5">E-post *</label>
                    <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            disabled={isSubmitting}
                            autoFocus
                            className="w-full pl-10 pr-3 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent disabled:bg-gray-50 transition-all"
                            placeholder="din.email@exempel.se"
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={isSubmitting || !email}
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#1A4D4F] text-[#f5f5f5] font-semibold rounded-xl hover:bg-[#1A4D4F]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                    {isSubmitting ? 'Skickar...' : 'Skicka återställningslänk'}
                </button>

                <Link
                    to="/"
                    className="flex items-center justify-center gap-1.5 text-xs text-[#1A4D4F] hover:underline font-medium pt-1"
                >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Tillbaka till inloggning
                </Link>
            </form>
        </AuthShell>
    );
};

export default ForgotPasswordPage;
